import * as React from 'react';
import { useState } from 'react';
import { PlayerTableViewModelV2 } from 'shared';
import { Button } from '../../ui/Button';
import { Modal } from '../../ui/Modal';
import { token } from '../../ui/theme';

/*
 * SetHistoryPeek — a "Set history" button (lives in the GameInfoPanel) that
 * opens a running log of every scored set so far: who bid, whether they made
 * it, and the resulting team scores. Mirrors the set-history table shown on the
 * game-over screen, but stays available mid-game. `setHistory` rides on every
 * game state (GameStateCore), so nothing shows until the first set is scored.
 */

interface Props {
  table: PlayerTableViewModelV2;
}

export const SetHistoryPeek: React.FC<Props> = ({ table }) => {
  const [open, setOpen] = useState(false);

  const gs = table.gameState;
  const setHistory = gs?.setHistory ?? [];
  if (setHistory.length === 0) return null;

  const teams = gs?.teams ?? [];
  const teamLabel = (teamId: string) => {
    const idx = teams.findIndex((t) => t.id === teamId);
    return idx >= 0 ? `Team ${idx + 1}` : '?';
  };
  const playerName = (playerId: string) =>
    table.tablePlayers.find((p) => p?.id === playerId)?.name ?? 'Unknown';

  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)} style={{ width: '100%' }}>
        Set history
      </Button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={<span style={{ letterSpacing: 2 }}>Set History</span>}
      >
        <div
          style={{
            background: token('color-surface'),
            border: `1px solid ${token('color-border')}`,
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          {setHistory.map((entry, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 14px',
                borderTop: i === 0 ? 'none' : `1px solid ${token('color-border')}`,
                fontSize: token('font-size-xs'),
              }}
            >
              <span style={{ color: token('color-muted') }}>#{i + 1}</span>
              <span style={{ color: token('color-text'), flex: 1, margin: '0 10px' }}>
                {teamLabel(entry.biddingTeamId)} ({playerName(entry.biddingPlayerId)}) bid{' '}
                {entry.bidForSet}{' '}
                <span
                  style={{
                    color: entry.madeBid ? token('color-win') : token('color-lose'),
                    fontWeight: 600,
                  }}
                >
                  {entry.madeBid ? 'made it' : 'set'}
                </span>
              </span>
              <span style={{ color: token('color-muted'), whiteSpace: 'nowrap' }}>
                {entry.scores.map((s) => s.score).join(' – ')}
              </span>
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
};

export default SetHistoryPeek;
